import React, { lazy, Suspense, useState, useRef, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext.tsx';
import { useReactToPrint } from 'react-to-print';
import { ToastContainer, toast } from 'react-toastify';

// CSS imports
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import './components/styles/customAccordion.css';
import './rebrand.css';
import './routeStyles';

// Sub-modules
import { Tooltip } from 'bootstrap';
import { loadPdfExportTools } from './utils/pdfTools';
import DeferredMount from './components/shared/DeferredMount';

// Components - Layout
import Aside from './components/appLayoutComponents/Aside.js';
import Breadcrumbs from './components/appLayoutComponents/Breadcrumbs.js';
import DrawerComponent from './components/appLayoutComponents/Drawer.js';
import TopPanel from './components/appLayoutComponents/TopPanel.js';
import UtilityRail from './components/appLayoutComponents/UtilityRail.tsx';

// Components - Drawer contexts
import EbusinessCard from './components/drawerContents/EbusinessCard';
import { API_BASE } from './config/apiBase';
import { useReports } from './hooks/useReports';
import { useCustomers } from './hooks/useCustomers';
import { useFolders } from './hooks/useFolders';
import { useUser } from './hooks/useUser';
import { useMobileView } from './hooks/isMobileView';

const ProfilePage = lazy(() => import('./components/drawerContents/ProfilePage.js'));
const AddCustomerModal = lazy(() => import('./components/modals/AddCustomerModal.js'));
const SendEmailReportModal = lazy(() => import('./components/modals/SendEmailReportModal.js'));
const PrefillReportModal = lazy(() => import('./components/modals/PrefillReportModal.js'));

// Components - Modules
const Dashboard = lazy(() => import('./components/Dashboard'));
const ProjectDashboard = lazy(() => import('./components/Projects'));
const PriceQuote = lazy(() => import('./components/modules/priceQuotes/PriceQuote.tsx'));
const Contracts = lazy(() => import('./components/modules/contracts/Contracts.tsx'));
const FLWRKFinance = lazy(() => import('./components/modules/finance/FLWRKFinance.js'));
const JobSeeker = lazy(() => import('./components/modules/jobseeker/JobSeeker.tsx'));
const CalculatorsTools = lazy(() => import('./components/modules/calculators/CalculatorsTools.tsx'));
const VerifyEmail = lazy(() => import('./components/registration/VerifyEmail'));

// Pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const CalendarPage = lazy(() => import('./components/modules/calendar/Calendar.js'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const Login = lazy(() => import('./components/login/Login'));
const LoginSuccess = lazy(() => import('./components/login/LoginSuccess'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const Register = lazy(() => import('./components/registration/Register'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const ReportsOverview = lazy(() => import('./components/modules/reports/ReportsOverview.js'));
const InvoiceList = lazy(() => import('./components/modules/invoices/InvoiceList.tsx'));
const NewInvoice = lazy(() => import('./components/modules/invoices/NewInvoice.tsx'));
const EditInvoice = lazy(() => import('./components/modules/invoices/EditInvoice.tsx'));

const AdminPage = lazy(() => import('./pages/AdminPage'));

const ROUTES_WITH_OWN_PAGE_HEADER = new Set([
  '/dashboard',
  '/reports',
  '/calendar',
  '/jobseeker',
  '/projects',
  '/price-quotes',
  '/contracts',
  '/calculators-tools',
  '/finance',
]);

function App() {
  const { t } = useTranslation();
  const { token, userId, role, login, logout} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const API = API_BASE;
  
  // State
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showEmailReportModal, setShowEmailReportModal] = useState(false);
  const [showPrefillReportModal, setShowPrefillReportModal] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isReportLocked, setIsReportLocked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAsideShown, setIsAsideShown] = useState(() => window.innerWidth >= 992);
  const [drawerContent, setDrawerContent] = useState(null);
  const [rows, setRows] = useState([]);
  const [currentSection, setCurrentSection] = useState(t('dashboard'));
  const [isProjectSelected, setIsProjectSelected] = useState(false);
  const [isPriceQuoteSelected, setIsPriceQuoteSelected] = useState(false);
  const [isFinanceSelected, setIsFinanceSelected] = useState(false);
  const [selectedProjectName, setSelectedProjectName] = useState('Moje projekty');
  const [selectedPriceQuoteName, setSelectedPriceQuoteName] = useState('Moje cenové ponuky');
  const [selectedFinanceName, setSelectedFinanceName] = useState(t('finance_title'));
  const [isJobseekerSelected, setIsJobseekerSelected] = useState(false);
  const [selectedInvoicesName, setSelectedInvoicesName] = useState(t('invoices_title'));
  const [isInvoicesSelected, setIsInvoicesSelected] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [reportForInvoice, setReportForInvoice] = useState(null);
  const [activeUtilityPanel, setActiveUtilityPanel] = useState(null);

  // Refs
  const printableRef = useRef();
  
  // Custom Hooks
  const isMobile = useMobileView(992);

  const {
    reports,
    setReports,
    selectedReportId,
    selectedReportName,
    isReportSelected,
    fetchReports,
    createReport,
    deleteReport,
    updateReportName,
    selectReport,
    markReportOpened,
    setSelectedReportName,
    setIsReportSelected
  } = useReports(token);

  const {
    customers,
    selectedCustomer,
    selectedCustomerName,
    isCustomerSelected,
    fetchCustomers,
    addCustomer,
    deleteCustomer,
    selectCustomer,
    clearSelectedCustomer,
    setSelectedCustomerName,
    setIsCustomerSelected,
    refreshCustomersFlag
  } = useCustomers(token);

  const {
    folders,
    setFolders,
    fetchFolders,
    createFolder,
    updateFolder,
    deleteFolder
  } = useFolders(token);

  const {
    user,
    username: userUsername,
    fetchUserDetails
  } = useUser(token, userId);

  // Effects
  useEffect(() => {
    if (token && userId) {
      fetchReports();
      fetchCustomers();
      fetchFolders();
      fetchUserDetails();
    }
  }, [token, userId, fetchReports, fetchCustomers, fetchFolders, fetchUserDetails]);

  useEffect(() => {
    const handleTokenExpired = () => {
      toast.info(t('session_expired_please_log_in_again'), {
        position: 'bottom-left',
        toastId: 'session-expired'
      });
      navigate('/login', { replace: true });
    };

    window.addEventListener('tokenExpired', handleTokenExpired);
    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
    };
  }, [navigate, t]);

  // Add useEffect to handle initial translation
  useEffect(() => {
    setCurrentSection(t('dashboard'));
    setSelectedFinanceName(t('finance_title'));
  }, [t]);

  // Add useEffect to handle route changes
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/reports/')) {
      const routeReportId = path.split('/')[2];
      const routeReport = reports.find((report) => String(report.report_id) === String(routeReportId));
      const reportTitle = routeReport?.name
        || (String(selectedReportId) === String(routeReportId) ? selectedReportName : '')
        || t('work_reports');

      setCurrentSection(reportTitle);
      setActiveSection('reports');

      if (routeReport && (!isReportSelected || String(selectedReportId) !== String(routeReportId))) {
        selectReport(routeReport.report_id, routeReport.name);
      }
      return;
    }

    // Reset all section states first
    setIsReportSelected(false);
    setIsCustomerSelected(false);
    setIsProjectSelected(false);
    setIsPriceQuoteSelected(false);
    setIsFinanceSelected(false);
    setIsJobseekerSelected(false);
    setIsInvoicesSelected(false);

    switch(path) {
      case '/dashboard':
        setCurrentSection(t('dashboard'));
        setActiveSection('dashboard');
        break;
      case '/projects':
        setIsProjectSelected(true);
        setSelectedProjectName('Moje projekty');
        setCurrentSection(t('projects_title'));
        setActiveSection('projects');
        break;
      case '/calendar':
        setCurrentSection(t('calendar_title'));
        setActiveSection('calendar');
        break;
      case '/reports':
        setCurrentSection(t('work_reports'));
        setActiveSection('reports');
        break;
      case '/customers':
        setCurrentSection(t('customers'));
        setActiveSection('customers');
        break;
      case '/price-quotes':
        setIsPriceQuoteSelected(true);
        setSelectedPriceQuoteName('Moje cenové ponuky');
        setCurrentSection(t('price_quote_title'));
        setActiveSection('price_quotes');
        break;
      case '/finance':
        setIsFinanceSelected(true);
        setSelectedFinanceName(t('my_finance_title'));
        setCurrentSection(t('finance_title'));
        setActiveSection('finance');
        break;
      case '/jobseeker':
        setIsJobseekerSelected(true);
        setCurrentSection(t('jobseeker_title'));
        setActiveSection('jobseeker');
        break;
      case '/invoices':
        setIsInvoicesSelected(true);
        setSelectedInvoicesName(t('my_invoices_title'));
        setCurrentSection(t('invoices_title'));
        setActiveSection('invoices');
        break;
      case '/contracts':
        setCurrentSection('Contracts & Agreements');
        setActiveSection('contracts');
        break;
      default:
        setCurrentSection(t('dashboard'));
        setActiveSection('dashboard');
    }
  }, [
    isReportSelected,
    location.pathname,
    reports,
    selectReport,
    selectedReportId,
    selectedReportName,
    setIsCustomerSelected,
    setIsReportSelected,
    t
  ]);

  useEffect(() => {
    setIsAsideShown(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !isAsideShown) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsAsideShown(false);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAsideShown, isMobile]);

  // Handlers
  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
  });

  const handleDownloadPDF = async () => {
    const input = printableRef.current;
    if (!input || isPdfGenerating) return;

    const originalWidth = input.style.width;
    setIsPdfGenerating(true);

    try {
      const { jsPDF, html2canvas } = await loadPdfExportTools();
      // Render the same white printable view used by the browser print flow,
      // but at a predictable A4-friendly width.
      input.style.width = '794px';
      await document.fonts?.ready;

      const inputRect = input.getBoundingClientRect();
      const safeBreakOffsets = Array.from(input.querySelectorAll([
        '.print-report-table tbody tr',
        '.print-report-divider',
        '.print-report-project-row',
        '.print-report-footer'
      ].join(','))).map((element) => element.getBoundingClientRect().top - inputRect.top);

      const canvas = await html2canvas(input, {
        backgroundColor: '#ffffff',
        logging: false,
        scale: Math.min(window.devicePixelRatio || 1, 1.5),
        useCORS: true,
        windowWidth: 794
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const pageHeightPx = Math.floor((canvas.width * pdfHeight) / pdfWidth);
      const captureScale = canvas.width / inputRect.width;
      const safeBreaksPx = safeBreakOffsets
        .map((offset) => Math.floor(offset * captureScale))
        .filter((offset) => offset > 0 && offset < canvas.height)
        .sort((first, second) => first - second);
      let sourceY = 0;
      let pageIndex = 0;

      while (sourceY < canvas.height) {
        const naturalEnd = Math.min(sourceY + pageHeightPx, canvas.height);
        let sliceEnd = naturalEnd;

        if (naturalEnd < canvas.height) {
          const minimumUsefulPageEnd = sourceY + (pageHeightPx * 0.45);
          const availableBreaks = safeBreaksPx
            .filter((offset) => offset > minimumUsefulPageEnd && offset <= naturalEnd - 2);
          const safeBreak = availableBreaks[availableBreaks.length - 1];

          if (safeBreak) sliceEnd = safeBreak;
        }

        const sliceHeight = sliceEnd - sourceY;
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;
        const pageContext = pageCanvas.getContext('2d');

        if (!pageContext) throw new Error('PDF canvas context is unavailable.');

        pageContext.fillStyle = '#ffffff';
        pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageContext.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight
        );

        if (pageIndex > 0) pdf.addPage();
        const renderedHeight = (sliceHeight * pdfWidth) / canvas.width;
        pdf.addImage(
          pageCanvas.toDataURL('image/jpeg', 0.82),
          'JPEG',
          0,
          0,
          pdfWidth,
          renderedHeight,
          undefined,
          'FAST'
        );

        pageCanvas.width = 0;
        pageCanvas.height = 0;
        sourceY = sliceEnd;
        pageIndex += 1;
      }

      const safeReportName = (selectedReportName || 'work-report').replace(/[<>:"/\\|?*]+/g, '-');
      pdf.save(`${safeReportName}.pdf`);
      canvas.width = 0;
      canvas.height = 0;
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('pdf_export_error'), { position: 'bottom-left' });
    } finally {
      input.style.width = originalWidth;
      setIsPdfGenerating(false);
    }
  };

  const handleDownloadPriceQuotePDF = async () => {
    const input = document.getElementById('printableAreaQuote');
    if (!input) return;
    try {
      const { jsPDF, html2canvas } = await loadPdfExportTools();
      const canvas = await html2canvas(input, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('Cenova_ponuka.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleNavigation = (section) => {
    // Reset all section states
    setIsReportSelected(false);
    setIsCustomerSelected(false);
    setIsProjectSelected(false);
    setIsPriceQuoteSelected(false);
    setIsFinanceSelected(false);
    setIsJobseekerSelected(false);
    setIsInvoicesSelected(false);

    // Set the active section and its specific state
    setActiveSection(section);
    switch(section) {
      case 'dashboard':
        setCurrentSection(t('dashboard'));
        break;
      case 'projects':
        setIsProjectSelected(true);
        setSelectedProjectName('Moje projekty');
        setCurrentSection(t('projects_title'));
        break;
      case 'calendar':
        setCurrentSection(t('calendar_title'));
        break;
      case 'price_quotes':
        setIsPriceQuoteSelected(true);
        setSelectedPriceQuoteName('Moje cenové ponuky');
        setCurrentSection(t('price_quote_title'));
        break;
      case 'finance':
        setIsFinanceSelected(true);
        setSelectedFinanceName(t('my_finance_title'));
        setCurrentSection(t('finance_title'));
        break;
      case 'invoices':
        setIsInvoicesSelected(true);
        setSelectedInvoicesName(t('my_invoices_title'));
        setCurrentSection(t('invoices_title'));
        break;
      case 'contracts':
        setCurrentSection('Contracts & Agreements');
        break;
    }
  };

  // Replace individual handlers with the new unified handler
  const handleSelectDashboard = () => handleNavigation('dashboard');
  const handleSelectProjects = () => handleNavigation('projects');
  const handleSelectCalendar = () => handleNavigation('calendar');
  const handleSelectPriceQuotes = () => handleNavigation('price_quotes');
  const handleSelectFinance = () => handleNavigation('finance');

  const handleShowBusinessCard = () => {
    setDrawerContent(<EbusinessCard user={user} />);
    setIsDrawerOpen(true);
  };

  const handleToggleAside = () => {
    setIsAsideShown((prevValue) => !prevValue);
  };

  const handleCloseAside = () => {
    setIsAsideShown(false);
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setIsDrawerOpen(open);
  };

  const handleAutoOpenPrefillModal = () => {
    setShowPrefillReportModal(true);
  };  

  const handleToggleLock = () => {
    setIsReportLocked(prevState => !prevState);
    isReportLocked ? toast.success(t('report_unlocked'), { position: "bottom-left" }) : toast.success(t('report_locked'), { position: "bottom-left" });
  };

  const isWeekend = (date) => {
    const day = new Date(date).getDay();
    return day === 0 || day === 6;
  };

  const calculateHours = (from, to) => {
    if (!from || !to) return 0;
    const fromTime = new Date(`1970-01-01T${from}:00`);
    const toTime = new Date(`1970-01-01T${to}:00`);
    const diff = (toTime - fromTime) / (1000 * 60 * 60);
    return diff > 0 ? diff : 0;
  };

  const handleShowProfile = () => {
    setDrawerContent(<ProfilePage onClose={handleCloseProfile} onUpdate={handleProfileUpdate} />);
    setIsDrawerOpen(true);
  };

  const handleCloseProfile = () => {
    setIsDrawerOpen(false);
  };

  const handleProfileUpdate = (updatedProfile) => {
    setProfileData(updatedProfile);
  };

  const handleUpdateReportName = (updatedReport) => {
    if (token) {
      const savePayload = {
        name: updatedReport.name,
      };

      fetch(`${API}/reports/${updatedReport.report_id}/name`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(savePayload),
      })
      .then(() => {
        setReports(reports.map(report =>
          report.report_id === updatedReport.report_id ? { ...report, name: updatedReport.name } : report
        ));
        if (selectedReportId === updatedReport.report_id) {
          setSelectedReportName(updatedReport.name);
        }
        toast.success(t('report_name_updated'), { position: "bottom-left" });
      })
      .catch(error => {
        toast.error(t('error_updating_report_name'), { position: "bottom-left" });
      });
    }
  };

  const moveReportToFolder = (reportId, folderId) => {
    if (token) {
      fetch(`${API}/reports/${reportId}/folder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ folder_id: folderId }),
      })
      .then(response => {
        if (response.ok) {
          setReports(reports.map(report =>
            report.report_id === reportId ? { ...report, folder_id: folderId } : report
          ));
          toast.success(t('report_moved_to_folder'), { position: "bottom-left" });
        } else {
          throw new Error('Network response was not ok');
        }
      })
      .catch(error => {
        toast.error(t('error_moving_report_to_folder'), { position: "bottom-left" });
      });
    }
  };

  const handleSaveFolder = (updatedFolder) => {
    if (token) {
      fetch(`${API}/folders/${updatedFolder.folder_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: updatedFolder.name }),
      })
      .then(response => {
        if (response.ok) {
          setFolders(folders.map(folder =>
            folder.folder_id === updatedFolder.folder_id ? { ...folder, name: updatedFolder.name } : folder
          ));
          toast.success(t('folder_name_updated'), { position: "bottom-left" });
        } else {
          throw new Error('Network response was not ok');
        }
      })
      .catch(error => {
        toast.error(t('error_renaming_folder'), { position: "bottom-left" });
      });
    }
  };

  const handleCreateFolder = (newFolder) => {
    if (token) {
      fetch(`${API}/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newFolder),
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        setFolders([...folders, data]);
        toast.success(t('folder_created_successfully'), { position: "bottom-left" });
      })
      .catch(error => {
        toast.error(t('error_creating_folder'), { position: "bottom-left" });
      });
    }
  };

  const handleDeleteFolder = (folderId) => {
    if (token) {
      fetch(`${API}/folders/${folderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      })
      .then(() => {
        setFolders(folders.filter(folder => folder.folder_id !== folderId));
        toast.success(t('folder_deleted'), { position: "bottom-left" });
      })
      .catch(error => {
        toast.error(t('error_deleting_folder'), { position: "bottom-left" });
      });
    }
  };

  const handleCreateInvoiceFromReport = (prefillData) => {
    if (!prefillData) {
      toast.error("Nie sú dostupné dáta pre faktúru");
      return;
    }
    navigate("/invoices/new", { state: { prefill: prefillData } });
  };


  const handlePrepareInvoiceData = (prefillData) => {
    setReportForInvoice(prefillData);
  };

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    const tooltipList = [...tooltipTriggerList].map(el => new Tooltip(el));

    return () => {
      tooltipList.forEach(tooltip => tooltip.dispose());
    };
  }, []);

  return (
    <div className="App">
        {!token || ['/privacy-policy', '/terms-and-conditions', '/terms-of-use'].includes(location.pathname) ? (
        <Suspense fallback={null}>
        <Routes>
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
            <Route path="/terms-of-use" element={<Navigate to="/terms-and-conditions" replace />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login onLogin={login} />} />
            <Route path="/login-success" element={<LoginSuccess onLogin={login} />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
        ) : (
        <div className={`main-content app-content container-fluid d-flex px-0 ${isMobile ? 'app-content--mobile' : ''} ${isAsideShown ? 'app-content--aside-open' : ''}`}>
            {isMobile && isAsideShown && (
              <button
                type="button"
                className="app-aside-backdrop"
                onClick={handleCloseAside}
                aria-label={t('close_navigation')}
              />
            )}
            {isAsideShown && (
            <Aside
                reports={reports}
                folders={folders}
                customers={customers}
                handleSelectCustomer={selectCustomer}
                handleClearCustomerSelection={clearSelectedCustomer}
                handleSelectReport={selectReport}
                handleDeleteCustomer={deleteCustomer}
                handleDeleteReport={deleteReport}
                handleCreateNewReport={() => createReport(folders)}
                handleUpdateReportName={handleUpdateReportName}
                handleOpenCustomerModal={() => setShowCustomerModal(true)}
                onSaveFolder={updateFolder}
                onCreateFolder={createFolder}
                onDeleteFolder={deleteFolder}
                setSection={setCurrentSection}
                clearSelections={() => {
                setSelectedReportName('');
                setSelectedCustomerName('');
                }}
                logout={logout}
                username={userUsername}
                onShowBusinessCard={handleShowBusinessCard}
                moveReportToFolder={moveReportToFolder}
                role={role}
                onClose={isMobile ? handleCloseAside : undefined}
            />
            )}
            <main className="main-custom app-main">
            <TopPanel 
                onLogout={logout}
                username={userUsername}
                onShowBusinessCard={handleShowBusinessCard}
                onShowAside={handleToggleAside}
                onShowProfile={handleShowProfile}
            />
            <div className="report-details px-lg-5 px-3 w-100">
                {!ROUTES_WITH_OWN_PAGE_HEADER.has(location.pathname) && <Breadcrumbs
                handlePrint={handlePrint}
                handleDownloadPDF={handleDownloadPDF}
                isPdfGenerating={isPdfGenerating}
                handleOpenCustomerModal={() => setShowCustomerModal(true)}
                handleOpenEmailReportModal={() => setShowEmailReportModal(true)}
                isReportSelected={isReportSelected}
                isCustomerSelected={isCustomerSelected}
                onCreateNewReport={createReport}
                onDeleteCurrentReport={() => deleteReport(selectedReportId)}
                onToggleLock={handleToggleLock}
                isReportLocked={isReportLocked}
                reportName={selectedReportName}
                customerName={selectedCustomerName}
                projectName={selectedProjectName}
                isProjectSelected={isProjectSelected}
                financeName={selectedFinanceName}
                isFinanceSelected={isFinanceSelected}
                priceQuoteName={selectedPriceQuoteName}
                isPriceQuoteSelected={isPriceQuoteSelected}
                jobseekerName={"FLWRK Jobseeker"}
                isJobseekerSelected={isJobseekerSelected}
                invoicesName={selectedInvoicesName}
                isInvoicesSelected={isInvoicesSelected}
                currentSection={currentSection}
                folders={folders}
                onSaveFolder={handleSaveFolder}
                onCreateFolder={handleCreateFolder}
                onDeleteFolder={handleDeleteFolder}
                handleOpenPrefillRowsModal={() => setShowPrefillReportModal(true)}
                handleDownloadPriceQuotePDF={handleDownloadPriceQuotePDF}
                rows={rows}
                setRows={setRows}
                onCreateInvoiceFromReport={handleCreateInvoiceFromReport}
                reportForInvoice={reportForInvoice}
                />}
                <Suspense fallback={null}>
                <Routes>
                <Route path="/reports" element={
                    <ReportsOverview 
                    reports={reports}
                    folders={folders}
                    onSelectReport={selectReport}
                    onDeleteReport={deleteReport}
                    onCreateNewReport={() => createReport(folders)}
                    onUpdateReportName={handleUpdateReportName}
                    onMoveReportToFolder={moveReportToFolder}
                    onRenameFolder={updateFolder}
                    onSaveFolder={updateFolder}
                    onCreateFolder={createFolder}
                    onDeleteFolder={deleteFolder}
                    onPrepareInvoiceData={handlePrepareInvoiceData}
                    />} 
                />
                <Route path="/reports/:reportId" element={
                    <ReportsPage
                    selectedReportId={selectedReportId}
                    selectedReportName={selectedReportName}
                    handleCreateNewReport={() => createReport(folders)}
                    printableRef={printableRef}
                    isReportLocked={isReportLocked}
                    onAutoPrefillTrigger={handleAutoOpenPrefillModal}
                    handleOpenCustomerModal={() => setShowCustomerModal(true)}
                    refreshCustomersFlag={refreshCustomersFlag}
                    rows={rows}
                    setRows={setRows}
                    handleContentLoaded={() => {}}
                    onPrepareInvoiceData={handlePrepareInvoiceData}
                    onReportOpened={markReportOpened}
                    />}
                />
                <Route path="/dashboard" element={
                    <Dashboard
                    selectedReportId={selectedReportId}
                    selectedReportName={selectedReportName}
                    handleCreateNewReport={() => createReport(folders)}
                    handleSelectDashboard={handleSelectDashboard}
                    reports={reports}
                    onSelectReport={selectReport}
                    isReportLocked={isReportLocked}
                    rows={rows}
                    setRows={setRows}
                    />}
                />
                <Route path="/calendar" element={<CalendarPage handleSelectCalendar={handleSelectCalendar} />} />
                <Route path="/customers" element={
                  <CustomersPage
                    customers={customers}
                    selectedCustomer={selectedCustomer}
                    onSelectCustomer={selectCustomer}
                    onClearSelectedCustomer={clearSelectedCustomer}
                    onDeleteCustomer={deleteCustomer}
                    handleOpenCustomerModal={() => setShowCustomerModal(true)}
                  />
                } />
                <Route path="/price-quotes" element={<PriceQuote handleSelectPriceQuotes={handleSelectPriceQuotes} printableRef={printableRef} />} />
                <Route path="/contracts" element={<Contracts />} />
                <Route path="/projects" element={
                    <ProjectDashboard
                    selectedReportId={selectedReportId}
                    selectedReportName={selectedReportName}
                    handleCreateNewReport={() => createReport(folders)}
                    handleSelectProjects={handleSelectProjects}
                    isReportLocked={isReportLocked}
                    rows={rows}
                    setRows={setRows}
                    />}
                />
                <Route path="/finance" element={
                    <FLWRKFinance
                    selectedReportId={selectedReportId}
                    selectedReportName={selectedReportName}
                    handleCreateNewReport={() => createReport(folders)}
                    handleSelectFinance={handleSelectFinance}
                    isReportLocked={isReportLocked}
                    rows={rows}
                    setRows={setRows}
                    />}
                />
                <Route path="/jobseeker" element={
                    <JobSeeker/>}
                />
                <Route path="/calculators-tools" element={
                    <CalculatorsTools/>}
                />
                <Route path="/calculator-tools" element={<Navigate to="/calculators-tools" replace />} />
                <Route path="/profile" element={
                    <ProfilePage
                    profileData={profileData}
                    onUpdate={handleProfileUpdate}
                    />
                } />
                <Route path="/invoices" element={<InvoiceList />} />
                <Route path="/invoices/new" element={<NewInvoice customers={customers} />} />
                <Route path="/invoices/:id" element={<EditInvoice customers={customers} />} />
                {role == 'admin' && (
                    <Route path="/admin" element={<AdminPage />} />
                )}
                <Route path="*" element={<Navigate to="/dashboard" />} />
                </Routes>
                </Suspense>
            </div>
            <DeferredMount active={showCustomerModal}>
            <AddCustomerModal
                show={showCustomerModal}
                handleClose={() => setShowCustomerModal(false)}
                onAddCustomer={addCustomer}
            />
            </DeferredMount>
            <DeferredMount active={showEmailReportModal}>
            <SendEmailReportModal
                show={showEmailReportModal}
                handleClose={() => setShowEmailReportModal(false)}
                printableRef={printableRef}
                reportName={selectedReportName}
            />
            </DeferredMount>
            <DeferredMount active={showPrefillReportModal}>
            <PrefillReportModal
                show={showPrefillReportModal}
                handleClose={() => setShowPrefillReportModal(false)}
                onSubmitPrefill={(options) => {
                const { applyTo, from, to, project } = options;

                if (!Array.isArray(rows) || rows.length === 0) {
                    toast.error('No report data available to prefill', { position: "bottom-left" });
                    return;
                }

                const updatedRows = rows.map(row => {
                    const isWeekendDay = isWeekend(row.date);
                    const shouldApply = applyTo === 'all' || (applyTo === 'workdays' && !isWeekendDay);

                    if (!shouldApply) return row;

                    const updatedRow = {
                    ...row,
                    from,
                    to,
                    project,
                    };
                    updatedRow.hourCount = calculateHours(from, to);
                    return updatedRow;
                });

                setRows(updatedRows);
                toast.success('Rows have been prefilled', { position: "bottom-left" });
                setShowPrefillReportModal(false);
                }}
            />
            </DeferredMount>

            <DrawerComponent isOpen={isDrawerOpen} toggleDrawer={toggleDrawer}>
              <Suspense fallback={null}>{drawerContent}</Suspense>
            </DrawerComponent>
            </main>
            <UtilityRail
              activePanel={activeUtilityPanel}
              onPanelChange={setActiveUtilityPanel}
              username={userUsername}
              onLogout={logout}
              onShowBusinessCard={handleShowBusinessCard}
              onShowProfile={handleShowProfile}
            />
        </div>
        )}
        <ToastContainer />
    </div>
  );
}

export default App;
